import { useEffect, useMemo, useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  Spinner,
} from '@/components/ui';
import { useTabAnalyzerPrompts } from '@/hooks/use-tab-analyzer-prompts';
import { PromptVersionHistory } from '@/components/admin/PromptVersionHistory';
import type {
  CadenceOption,
  IOverviewHighlight,
  ITabAnalyzerPrompt,
  TabId,
  WindowOption,
} from '@skystone/shared';

/**
 * Admin "AI Analyzers" tab content (PRODOPSAI-407).
 *
 * Surfaces an editable system prompt per LLM-as-analyst tab. Supports:
 *   - inline edit + Save
 *   - Reset to default (repopulates the textarea from defaultSystemPrompt
 *     without persisting)
 *   - Test prompt (dry-run with the textarea contents AND a small filter
 *     strip; the test result panel shows the 5 insights returned)
 *
 * Only the SiteAdmin / DataQualityAdmin route should mount this component;
 * the API will reject unauthenticated callers anyway, but the route guard
 * avoids a confusing 403 banner inside the page.
 */

const TAB_LABEL: Record<TabId, string> = {
  quality_control: 'Quality Control',
};

const WINDOW_OPTIONS: readonly WindowOption[] = ['3m', '6m', '12m'];
const CADENCE_OPTIONS: readonly CadenceOption[] = ['monthly', 'weekly'];

function formatUpdatedAt(iso: string): string {
  const ts = Date.parse(iso);
  if (!Number.isFinite(ts) || ts === 0) return 'never edited';
  const d = new Date(ts);
  return d.toISOString().slice(0, 16).replace('T', ' ');
}

interface PromptCardProps {
  row: ITabAnalyzerPrompt;
  onSave: (tabId: TabId, prompt: string) => Promise<void>;
  onTest: (
    tabId: TabId,
    prompt: string,
    window: WindowOption,
    cadence: CadenceOption,
  ) => Promise<IOverviewHighlight[] | null>;
  /** Refetch the prompt list after a version restore re-seeds the live row. */
  onRestored: () => void;
}

function TabPromptCard({ row, onSave, onTest, onRestored }: Readonly<PromptCardProps>) {
  const [draft, setDraft] = useState(row.systemPrompt);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveError, setSaveError] = useState<string | null>(null);
  const [testStatus, setTestStatus] = useState<'idle' | 'running' | 'done' | 'error'>('idle');
  const [testInsights, setTestInsights] = useState<IOverviewHighlight[] | null>(null);
  const [testError, setTestError] = useState<string | null>(null);
  const [window, setWindow] = useState<WindowOption>('6m');
  const [cadence, setCadence] = useState<CadenceOption>('monthly');

  // When the upstream row changes (e.g. after a save), seed the draft.
  useEffect(() => {
    setDraft(row.systemPrompt);
  }, [row.systemPrompt]);

  const isDirty = draft !== row.systemPrompt;
  const draftIsEmpty = draft.trim().length === 0;

  async function handleSave() {
    setSaveStatus('saving');
    setSaveError(null);
    try {
      await onSave(row.tabId, draft);
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus('idle'), 2000);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Save failed';
      setSaveError(message);
      setSaveStatus('error');
    }
  }

  function handleResetToDefault() {
    setDraft(row.defaultSystemPrompt);
  }

  async function handleTest() {
    setTestStatus('running');
    setTestError(null);
    setTestInsights(null);
    try {
      const insights = await onTest(row.tabId, draft, window, cadence);
      setTestInsights(insights);
      setTestStatus('done');
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Test failed';
      setTestError(message);
      setTestStatus('error');
    }
  }

  return (
    <Card data-testid={`tab-prompt-card-${row.tabId}`}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-bold text-foreground">{TAB_LABEL[row.tabId]}</h3>
          <Badge variant="secondary">tab: {row.tabId}</Badge>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground" htmlFor={`prompt-${row.tabId}`}>
          System prompt
        </label>
        <textarea
          id={`prompt-${row.tabId}`}
          data-testid={`tab-prompt-textarea-${row.tabId}`}
          rows={20}
          className="w-full rounded-md border border-border bg-background p-3 font-mono text-xs"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
        />
        <p className="text-xs text-muted-foreground">
          Last edited: {formatUpdatedAt(row.updatedAt)}
          {row.updatedBy ? ` by ${row.updatedBy}` : ''}
        </p>
        <p className="text-xs text-muted-foreground">
          Available context: window (3m/6m/12m modes), cadence, scope, currentPeriod, previousPeriod,
          per-subject windowStats (complete-period min/max/mean/monthsOnTarget), and — once the
          current month is mature — paceAdjusted / intraMonth for provisional "so far" reads.
        </p>
        <div className="flex flex-wrap items-center gap-2 border-t border-border pt-3">
          <label className="text-xs text-muted-foreground" htmlFor={`window-${row.tabId}`}>
            Window
          </label>
          <select
            id={`window-${row.tabId}`}
            className="rounded-md border border-border bg-background px-2 py-1 text-xs"
            value={window}
            onChange={(e) => setWindow(e.target.value as WindowOption)}
          >
            {WINDOW_OPTIONS.map((w) => (
              <option key={w} value={w}>{w}</option>
            ))}
          </select>
          <label className="text-xs text-muted-foreground" htmlFor={`cadence-${row.tabId}`}>
            Cadence
          </label>
          <select
            id={`cadence-${row.tabId}`}
            className="rounded-md border border-border bg-background px-2 py-1 text-xs"
            value={cadence}
            onChange={(e) => setCadence(e.target.value as CadenceOption)}
          >
            {CADENCE_OPTIONS.map((c) => (
              <option key={c} value={c}>{c}</option>
            ))}
          </select>
          <div className="ml-auto flex items-center gap-2">
            <Button
              type="button"
              variant="secondary"
              onClick={handleTest}
              disabled={testStatus === 'running' || draftIsEmpty}
              data-testid={`tab-prompt-test-${row.tabId}`}
            >
              {testStatus === 'running' ? 'Testing…' : 'Test prompt'}
            </Button>
            <Button
              type="button"
              variant="secondary"
              onClick={handleResetToDefault}
              data-testid={`tab-prompt-reset-${row.tabId}`}
            >
              Reset to default
            </Button>
            <Button
              type="button"
              onClick={handleSave}
              disabled={!isDirty || saveStatus === 'saving' || draftIsEmpty}
              data-testid={`tab-prompt-save-${row.tabId}`}
            >
              {saveStatus === 'saving' ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
        {saveStatus === 'saved' && (
          <p className="text-xs text-success-700" aria-live="polite">Saved</p>
        )}
        {saveStatus === 'error' && saveError && (
          <p className="text-xs text-error" role="alert">{saveError}</p>
        )}
        {testStatus !== 'idle' && (
          <TestResultPanel
            status={testStatus}
            insights={testInsights}
            error={testError}
            tabId={row.tabId}
          />
        )}
        <PromptVersionHistory tabId={row.tabId} onRestored={onRestored} />
      </CardContent>
    </Card>
  );
}

interface TestResultPanelProps {
  status: 'idle' | 'running' | 'done' | 'error';
  insights: IOverviewHighlight[] | null;
  error: string | null;
  tabId: TabId;
}

function TestResultPanel({ status, insights, error, tabId }: Readonly<TestResultPanelProps>) {
  if (status === 'running') {
    return (
      <div className="flex items-center gap-2 rounded-md border border-border bg-background p-3" data-testid={`tab-prompt-test-result-${tabId}`}>
        <Spinner /> <span className="text-xs text-muted-foreground">Running analyzer…</span>
      </div>
    );
  }
  if (status === 'error') {
    return (
      <div className="rounded-md border border-error bg-background p-3" data-testid={`tab-prompt-test-result-${tabId}`}>
        <p className="text-xs text-error" role="alert">{error ?? 'Test failed'}</p>
      </div>
    );
  }
  if (insights === null) {
    return (
      <div className="rounded-md border border-border bg-background p-3" data-testid={`tab-prompt-test-result-${tabId}`}>
        <p className="text-xs text-muted-foreground">
          LLM unavailable (no API key, or the call errored). The Overview tab would render the empty-state copy.
        </p>
      </div>
    );
  }
  return (
    <div className="rounded-md border border-border bg-background p-3" data-testid={`tab-prompt-test-result-${tabId}`}>
      <p className="mb-2 text-xs font-semibold text-foreground">{insights.length} insight(s) returned</p>
      <ul className="space-y-2">
        {insights.map((item, i) => (
          <li
            key={`${item.metricKey}-${item.kind}-${i}`}
            data-testid={`tab-prompt-test-insight-${tabId}`}
            className="rounded-md bg-muted/30 p-2 text-xs"
          >
            <div className="font-bold">{item.subjectLabel}</div>
            <div>{item.magnitudeLabel}</div>
            {item.contextLabel && (
              <div className="italic text-muted-foreground">— {item.contextLabel}</div>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

export function TabAnalyzerPromptsEditor() {
  const { prompts, isLoading, error, updatePrompt, testPrompt, refetch } = useTabAnalyzerPrompts();

  // Memoise so the rendered list doesn't re-create when the surrounding page
  // (admin metric definitions) re-renders.
  const rows = useMemo(() => prompts, [prompts]);

  if (isLoading) {
    return (
      <section className="space-y-3" aria-labelledby="tab-analyzers-heading">
        <h2 id="tab-analyzers-heading" className="text-sm font-bold text-foreground">
          AI Analyzers
        </h2>
        <div className="flex items-center justify-center py-6"><Spinner /></div>
      </section>
    );
  }

  return (
    <section className="space-y-3" aria-labelledby="tab-analyzers-heading">
      <div>
        <h2 id="tab-analyzers-heading" className="text-sm font-bold text-foreground">
          AI Analyzers
        </h2>
        <p className="text-xs text-muted-foreground">
          Edit the system prompt the LLM analyst uses when it picks 5 highlights from the Quality Control dossier. Test before you save.
        </p>
      </div>
      {error && (
        <Card>
          <CardContent className="p-3">
            <p className="text-xs text-error" role="alert">{error}</p>
          </CardContent>
        </Card>
      )}
      <div className="space-y-3">
        {rows.map((row) => (
          <TabPromptCard
            key={row.tabId}
            row={row}
            onSave={async (tabId, prompt) => {
              await updatePrompt(tabId, prompt);
            }}
            onTest={(tabId, prompt, window, cadence) =>
              testPrompt(tabId, { systemPrompt: prompt, window, cadence })
            }
            onRestored={refetch}
          />
        ))}
      </div>
    </section>
  );
}
